"use client";

// The "right now" verdict for TODAY's Panchang — the one thing a SERP answer
// box structurally cannot do (it shows the five limbs; it does not know which
// window is running for the seeker's city as they read).
//
// Rule 25/29 discipline: every displayed value is a backend string from the
// choghadiya engine response. The only client arithmetic is comparing
// Date.now() against the engine's own `starts_at`/`ends_at` instants — the
// exact contract those fields exist for ("the instants a client may count
// down to", ChoghadiyaPeriod). The verdict wording maps the engine's own
// quality classification; nothing here re-derives an astronomical value.
//
// No CTA: the governed resultOffer slot owns this surface's single primary
// action (rules 39/46) — the strip is verdict, not offer.

import { useEffect, useState } from "react";

import { fetchChoghadiya } from "@/lib/api/panchang";
import type { ChoghadiyaPeriod, ChoghadiyaResponse } from "@/lib/api/panchang";
import type { CityData } from "@/lib/cities-data.types";

const VERDICT: Record<string, { en: string; hi: string }> = {
  best: { en: "an excellent window for new work", hi: "नए काम के लिए उत्तम समय" },
  good: { en: "a shubh window for new work", hi: "नए काम के लिए शुभ समय" },
  neutral: { en: "a neutral window", hi: "सामान्य समय" },
  bad: { en: "best avoided for new beginnings", hi: "नया काम टालना उचित" },
};

/** The window containing `nowMs`, by the engine's countdown instants first. */
export function currentChoghadiyaPeriod(
  response: Pick<ChoghadiyaResponse, "day_choghadiya" | "night_choghadiya">,
  nowMs: number
): ChoghadiyaPeriod | null {
  const all = [...response.day_choghadiya, ...response.night_choghadiya];
  const byInstant = all.find((period) => {
    if (!period.starts_at || !period.ends_at) return false;
    const start = Date.parse(period.starts_at);
    const end = Date.parse(period.ends_at);
    return Number.isFinite(start) && Number.isFinite(end) && start <= nowMs && nowMs < end;
  });
  // A cached response's `is_current` flag ages inside the edge TTL; the
  // instants never do. The flag is only the fallback for legacy payloads
  // that predate `starts_at`/`ends_at`.
  return byInstant ?? all.find((period) => period.is_current) ?? null;
}

export default function PanchangNowStrip({
  city,
  isHindi,
  todayForCity,
  rahuKaal,
}: {
  city: CityData;
  isHindi: boolean;
  todayForCity: string;
  rahuKaal: { start: string; end: string } | null;
}) {
  const [response, setResponse] = useState<ChoghadiyaResponse | null>(null);
  const [nowMs, setNowMs] = useState(() => Date.now());

  useEffect(() => {
    let cancelled = false;
    fetchChoghadiya({
      date: todayForCity,
      latitude: city.latitude,
      longitude: city.longitude,
    })
      .then((result) => {
        if (!cancelled) setResponse(result);
      })
      .catch(() => {
        // Verdict is enhancement, never a gate: the full Panchang below is
        // the page's core value and renders regardless.
      });
    return () => {
      cancelled = true;
    };
  }, [city.latitude, city.longitude, todayForCity]);

  // Re-pick the window on a coarse tick so the verdict rolls over at a
  // boundary without a reload; 60s is far finer than any ~90min window.
  useEffect(() => {
    const tick = window.setInterval(() => setNowMs(Date.now()), 60_000);
    return () => window.clearInterval(tick);
  }, []);

  const period = response ? currentChoghadiyaPeriod(response, nowMs) : null;
  const verdict = period ? VERDICT[period.quality] : null;

  // The container always reserves its height while data loads so the strip
  // never shifts the result stack on this CWV-golden route (rule 58).
  return (
    <div className="min-h-[88px]" data-panchang-now-strip="">
      {period && verdict ? (
        <section
          aria-label={isHindi ? "अभी का चौघड़िया" : "Right now"}
          className="card-sacred border-l-4 border-border-interactive p-4"
        >
          <p className="text-eyebrow font-semibold uppercase tracking-eyebrow text-text-muted">
            {isHindi ? `अभी — ${city.name_hi ?? city.name}` : `Right now — ${city.name}`}
          </p>
          <p className="mt-1 text-body font-semibold text-text-primary">
            {isHindi
              ? `${period.name_hi} चौघड़िया चल रहा है — ${verdict.hi} · ${period.end_time} तक`
              : `${period.name} choghadiya is running — ${verdict.en} · until ${period.end_time}`}
          </p>
          {rahuKaal ? (
            <p className="mt-1 text-body-small text-text-secondary">
              {isHindi
                ? `आज राहु काल: ${rahuKaal.start} – ${rahuKaal.end}`
                : `Rahu Kaal today: ${rahuKaal.start} – ${rahuKaal.end}`}
            </p>
          ) : null}
        </section>
      ) : null}
    </div>
  );
}
